import { renderLocationSidebar } from './locationSidebar';

export const CREATE_USER_TEMPLATE = `
<div class="flex min-h-screen overflow-hidden bg-background-light">
${renderLocationSidebar('users')}
<main class="ml-64 flex-1 flex flex-col overflow-y-auto">
<header class="bg-white border-b border-slate-200 px-8 py-4 flex flex-col gap-1">
  <nav class="flex items-center gap-2 text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
    <a class="hover:text-primary transition-colors" href="/users">Users</a>
    <span class="material-symbols-outlined text-xs">chevron_right</span>
    <span id="create-user-breadcrumb-label" class="text-slate-600">Create New User</span>
  </nav>
  <h1 id="create-user-page-title" class="text-2xl font-extrabold text-slate-900 tracking-tight">Create New User</h1>
  <p class="text-sm text-slate-500">Configure profile, account roles, and permissions for a new team member.</p>
</header>

<div class="p-8 max-w-[1120px] mx-auto w-full">
  <div id="create-user-alert" class="hidden mb-6 items-start gap-4 p-4 rounded-xl border">
    <span id="create-user-alert-icon" class="material-symbols-outlined"></span>
    <div>
      <p id="create-user-alert-title" class="text-sm font-semibold"></p>
      <p id="create-user-alert-text" class="text-sm opacity-80"></p>
    </div>
  </div>

  <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
    <form id="create-user-form">
      <div class="p-8 border-b border-slate-100">
        <div class="text-lg font-bold mb-6 flex items-center gap-2">
          <span class="material-symbols-outlined text-primary">person</span>
          Profile Information
        </div>
        <div class="flex flex-col md:flex-row gap-8">
          <div class="flex flex-col items-center gap-4 w-[180px] shrink-0">
            <div class="relative group">
              <div class="size-32 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
                <img id="create-user-avatar-preview" class="hidden w-full h-full object-cover" alt="User avatar preview"/>
                <span id="create-user-avatar-placeholder" class="material-symbols-outlined text-slate-400 text-5xl">add_a_photo</span>
              </div>
              <input id="create-user-avatar-input" type="file" accept="image/*" class="hidden"/>
              <button id="create-user-avatar-trigger" class="absolute bottom-0 right-0 bg-primary text-white p-3 rounded-full shadow-lg hover:bg-primary/90 transition-all" type="button">
                <span class="material-symbols-outlined text-sm leading-none">edit</span>
              </button>
            </div>
            <span class="text-xs text-slate-500 text-center leading-5">Allowed JPG, GIF or PNG.<br/>Max size of 800kB</span>
          </div>

          <div class="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="space-y-2">
              <label class="text-sm font-bold text-slate-700" for="user-first-name">First Name</label>
              <input id="user-first-name" class="w-full bg-slate-50 border-slate-200 rounded-lg focus:ring-primary focus:border-primary transition-all p-3 text-sm text-slate-900 placeholder:text-slate-400" placeholder="e.g. John" type="text"/>
            </div>
            <div class="space-y-2">
              <label class="text-sm font-bold text-slate-700" for="user-last-name">Last Name</label>
              <input id="user-last-name" class="w-full bg-slate-50 border-slate-200 rounded-lg focus:ring-primary focus:border-primary transition-all p-3 text-sm text-slate-900 placeholder:text-slate-400" placeholder="e.g. Doe" type="text"/>
            </div>
            <div class="space-y-2">
              <label class="text-sm font-bold text-slate-700" for="user-email">Email Address</label>
              <input id="user-email" class="w-full bg-slate-50 border-slate-200 rounded-lg focus:ring-primary focus:border-primary transition-all p-3 text-sm text-slate-900 placeholder:text-slate-400" placeholder="john.doe@motojourney.com" type="email" required/>
            </div>
            <div class="space-y-2">
              <label class="text-sm font-bold text-slate-700" for="user-phone">Phone Number</label>
              <input id="user-phone" class="w-full bg-slate-50 border-slate-200 rounded-lg focus:ring-primary focus:border-primary transition-all p-3 text-sm text-slate-900 placeholder:text-slate-400" placeholder="+1 (555) 000-0000" type="tel"/>
            </div>
          </div>
        </div>
      </div>

      <div class="p-8 border-b border-slate-100 bg-slate-50/50">
        <h3 class="text-lg font-bold mb-6 flex items-center gap-2">
          <span class="material-symbols-outlined text-primary">manage_accounts</span>
          Account Settings
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="space-y-2">
            <label class="text-sm font-bold text-slate-700" for="user-role">User Role</label>
            <select id="user-role" class="w-full bg-slate-50 border-slate-200 rounded-lg focus:ring-primary focus:border-primary transition-all p-3 text-sm text-slate-900">
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
          </div>
          <div class="space-y-2">
            <label class="text-sm font-bold text-slate-700" for="user-password">Password</label>
            <input id="user-password" class="w-full bg-slate-50 border-slate-200 rounded-lg focus:ring-primary focus:border-primary transition-all p-3 text-sm text-slate-900 placeholder:text-slate-400" type="password" minlength="8"/>
          </div>
          <div class="space-y-2">
            <label class="text-sm font-bold text-slate-700" for="user-password-confirm">Confirm Password</label>
            <input id="user-password-confirm" class="w-full bg-slate-50 border-slate-200 rounded-lg focus:ring-primary focus:border-primary transition-all p-3 text-sm text-slate-900 placeholder:text-slate-400" type="password" minlength="8"/>
          </div>
        </div>
        <div class="mt-4">
          <label class="inline-flex items-center gap-3 py-1">
            <input id="user-is-active" class="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary" type="checkbox" checked/>
            <span class="text-sm text-slate-600">Active account</span>
          </label>
        </div>
      </div>

      <div class="p-8">
        <h3 class="text-lg font-bold mb-6 flex items-center gap-2">
          <span class="material-symbols-outlined text-primary">security</span>
          Permissions
        </h3>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <label class="relative flex items-start p-4 border rounded-xl border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
            <div class="flex items-center h-5"><input checked class="size-4 rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/></div>
            <div class="ml-3 text-sm"><span class="font-bold text-slate-900">Manage Rides</span><p class="text-xs text-slate-500">Create, edit and delete journey routes.</p></div>
          </label>
          <label class="relative flex items-start p-4 border rounded-xl border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
            <div class="flex items-center h-5"><input class="size-4 rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/></div>
            <div class="ml-3 text-sm"><span class="font-bold text-slate-900">View Analytics</span><p class="text-xs text-slate-500">Access reports and user behavior data.</p></div>
          </label>
          <label class="relative flex items-start p-4 border rounded-xl border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
            <div class="flex items-center h-5"><input class="size-4 rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/></div>
            <div class="ml-3 text-sm"><span class="font-bold text-slate-900">System Settings</span><p class="text-xs text-slate-500">Modify global application configurations.</p></div>
          </label>
          <label class="relative flex items-start p-4 border rounded-xl border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
            <div class="flex items-center h-5"><input checked class="size-4 rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/></div>
            <div class="ml-3 text-sm"><span class="font-bold text-slate-900">Billing Access</span><p class="text-xs text-slate-500">Manage subscriptions and payments.</p></div>
          </label>
          <label class="relative flex items-start p-4 border rounded-xl border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
            <div class="flex items-center h-5"><input class="size-4 rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/></div>
            <div class="ml-3 text-sm"><span class="font-bold text-slate-900">Support Chat</span><p class="text-xs text-slate-500">Respond to user tickets and messages.</p></div>
          </label>
          <label class="relative flex items-start p-4 border rounded-xl border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
            <div class="flex items-center h-5"><input class="size-4 rounded border-slate-300 text-primary focus:ring-primary" type="checkbox"/></div>
            <div class="ml-3 text-sm"><span class="font-bold text-slate-900">User Moderation</span><p class="text-xs text-slate-500">Ban, flag or verify community users.</p></div>
          </label>
        </div>
      </div>

      <div class="p-6 bg-slate-50 flex justify-end gap-3 border-t border-slate-100">
        <a href="/users" class="px-6 py-2.5 rounded-lg border border-slate-300 text-sm font-bold text-slate-700 hover:bg-white transition-all">Cancel</a>
        <button id="create-user-submit" class="px-6 py-2.5 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 shadow-md shadow-primary/20 transition-all flex items-center gap-2" type="submit">
          <span class="material-symbols-outlined text-sm">person_add</span>
          <span id="create-user-submit-label">Create User</span>
        </button>
      </div>
    </form>
  </div>
</div>
</main>
</div>
`;
